import styled from "styled-components";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { questionTypeQuestion } from "../constants/questionTypes";

const QuestionResponseSlider = ({ id, question, saveUpdatedAnswers, currentQuestionDet }) => {
  const [sliderValue, setSliderValue] = useState(5);
  const [isDragging, setIsDragging] = useState(false);
  const [clickedVal, setClickedVal] = useState(false);

  // stable refs
  const sliderValueRef = useRef(sliderValue);
  const lastSavedRef = useRef(null);
  const trackId = `slider-track-${id}`;

  // keep ref sync'd with state (so handlers read latest)
  useEffect(() => {
    sliderValueRef.current = sliderValue;
  }, [sliderValue]);

  const clamp = (v) => Math.min(Math.max(Math.round(v), 0), 10);

  // commit save once (guard against duplicates)
  const commitSave = useCallback(
    (valueToSave) => {
      const normalized = clamp(valueToSave);
      if (lastSavedRef.current === normalized) return;
      lastSavedRef.current = normalized;

      setClickedVal(true);

      const QuesResp = {
        id: question?.dimension,
        factor: question?.factor,
        q: question?.q,
        ques_order: question?.order,
        response: normalized,
        role: question?.role,
        reverse: question?.reverse || false,
        clicked: true,
        ques_sort_order: currentQuestionDet?.id,
      };

      // single call only on commit
      saveUpdatedAnswers(currentQuestionDet.id, QuesResp, questionTypeQuestion);
    },
    [question, saveUpdatedAnswers, currentQuestionDet]
  );

  // Attach move/up listeners only while dragging. No sliderValue in deps.
  useEffect(() => {
    if (!isDragging) return; // only attach while dragging

    const handleMouseMove = (e) => {
      const track = document.getElementById(trackId);
      if (!track) return;
      const trackRect = track.getBoundingClientRect();
      let raw = ((e.clientX - trackRect.left) / trackRect.width) * 10;
      raw = Math.min(Math.max(raw, 0), 10);
      setSliderValue(raw); // visual update only
    };

    const handleMouseUp = (e) => {
      // commit once when user releases
      setIsDragging(false);
      const rounded = clamp(sliderValueRef.current);
      setSliderValue(rounded);
      commitSave(rounded);
    };

    const handleTouchMove = (e) => {
      if (!e.touches?.[0]) return;
      const track = document.getElementById(trackId);
      if (!track) return;
      const trackRect = track.getBoundingClientRect();
      let raw = ((e.touches[0].clientX - trackRect.left) / trackRect.width) * 10;
      raw = Math.min(Math.max(raw, 0), 10);
      setSliderValue(raw);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      const rounded = clamp(sliderValueRef.current);
      setSliderValue(rounded);
      commitSave(rounded);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, commitSave, trackId]); // note: sliderValue NOT in deps

  // initialize / restore from server value
  useEffect(() => {
    if (currentQuestionDet?.response && typeof currentQuestionDet.response.response !== "undefined") {
      const restored = clamp(currentQuestionDet.response.response);
      setSliderValue(restored);
      setClickedVal(true);
      lastSavedRef.current = restored;
      sliderValueRef.current = restored;
    } else {
      setSliderValue(5);
      setClickedVal(false);
      lastSavedRef.current = null;
      sliderValueRef.current = 5;
    }
  }, [currentQuestionDet]);

  // click on track commits immediately
  const handleClick = (e) => {
    const track = document.getElementById(trackId);
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newValue = clamp((x / rect.width) * 10);
    setSliderValue(newValue);
    sliderValueRef.current = newValue;
    commitSave(newValue);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    // prevent text selection while dragging
    e.preventDefault();
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  // color & UI helpers (unchanged)
  const lerpColor = (color1, color2, t) => {
    const r = color1.r + t * (color2.r - color1.r);
    const g = color1.g + t * (color2.g - color1.g);
    const b = color1.b + t * (color2.b - color1.b);
    return { r, g, b };
  };

  const getThumbColor = () => {
    const color1 = { r: 248, g: 168, b: 176 };
    const color2 = { r: 254, g: 205, b: 141 };
    const color3 = { r: 149, g: 230, b: 210 };
    const t = sliderValue / 10;

    if (t < 0.5232) {
      const newT = t / 0.5232;
      const interpolatedColor = lerpColor(color1, color2, newT);
      return `rgb(${interpolatedColor.r}, ${interpolatedColor.g}, ${interpolatedColor.b})`;
    } else {
      const newT = (t - 0.5232) / (1 - 0.5232);
      const interpolatedColor = lerpColor(color2, color3, newT);
      return `rgb(${interpolatedColor.r}, ${interpolatedColor.g}, ${interpolatedColor.b})`;
    }
  };

  const thumbPosition = {
    left: `calc(${(sliderValue / 10) * 100}% - 10px )`,
  };

  const dotCount = 11;
  const dots = Array.from({ length: dotCount }, (_, index) => (
    <Dot key={index} style={{ left: `${(index / (dotCount - 1)) * 100}%` }} id={`dot_${index / (dotCount - 1) * 10}_${id}`} />
  ));

  return (
    <Container>
      <TrackWrapper onClick={handleClick} onMouseDown={handleMouseDown} onTouchStart={handleTouchStart}>
        <Track id={trackId}>
          <DotsContainer id={`dots_${id}`}>{dots}</DotsContainer>
          <Thumb
            style={{
              ...thumbPosition,
              backgroundColor: getThumbColor(),
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            id={`thumb_${id}`}
          >
            <Tooltip clicked={clickedVal} id={`tip_${id}`}>
              {clamp(sliderValue)}
              <Triangle />
            </Tooltip>
          </Thumb>
        </Track>
      </TrackWrapper>

      <ScaleLabelsContainer>
        <ScaleLabel>{question?.scale ? question?.scale[0] : "Strongly Disagree"}</ScaleLabel>
        <ScaleLabel>Neutral</ScaleLabel>
        <ScaleLabel>{question?.scale ? question?.scale[1] : "Strongly Agree"}</ScaleLabel>
      </ScaleLabelsContainer>
    </Container>
  );
};

export default QuestionResponseSlider;

/* styled components (unchanged) */

const Container = styled.div`
  width:100%;
  padding-top:70px;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

const ScaleLabel = styled.span`
  font-size: 14px;
`;

const SliderContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const ScaleLabelsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const TrackWrapper = styled.div`
  position: relative;
  height: calc(10px + 10px);
  cursor: pointer;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

const Track = styled.div`
  width: 100%;
  height: 6px;
  background: linear-gradient(
    to right,
    #F8A8B0 0%,
    #FECD8D 52.32%,
    #95E6D2 100%
  );
  position: relative;
  border-radius: 5px;
  cursor: pointer;
`;

const Thumb = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #000;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  user-select: none;
  border: 4px solid white;
  box-shadow: 0px 8px 20px rgba(63, 134, 254, 0.08);
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #666D79;
  border-radius: 8px;
  width: 33px;
  height: 29px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.clicked ? "#FFFFFF" : "#D6DBDF")};
  font-size: 14px;
  font-weight: ${(props) => (props.clicked ? "700" : "400")};
  margin-bottom: 8px;
`;

const Triangle = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 4px 4px 0;
  border-color: #666D79 transparent transparent;
`;

const DotsContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  display: flex;
  justify-content: space-between;
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  background-color: #fff;
  border-radius: 50%;
  position: absolute;
  transform: translateY(-50%);
`;
